import React, { useEffect, useMemo } from 'react';
import { Box, Chip, Dialog, DialogContent, Divider, Grow, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
import { CheckCircleFilled, CloseOutlined, SafetyCertificateOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { alpha, useTheme } from '@mui/material/styles';

import useAuth from 'hooks/useAuth';
import Button from '../../components/ui/Button';
import { buildMcpCadences } from '../../utils/mcp';
import { BILLING_CYCLES, CHECKOUT_COPY, CHECKOUT_STEPS } from '../../lib/billing/config';
import { findActiveMcp } from '../../lib/billing/selectionFromSubscription';
import { intervalPriceLabel } from 'lib/billing';
import { formatCents } from '../account-settings/subscriptions/checkout-v2/format';
import { useBillingStore } from '../account-settings/subscriptions/checkout-v2/store/useBillingStore';
import { useCheckoutStore } from '../account-settings/subscriptions/checkout-v2/store/useCheckoutStore';
import { useCancelSubscription } from '../account-settings/subscriptions/checkout-v2/Configure/useCancelSubscription';
import { CancelConfirmModal } from '../account-settings/subscriptions/checkout-v2/Configure/CancelConfirmModal';
import { ctaSx, mcpFocusSx } from './mcpUi';
import { Eyebrow, ink, statusSx } from './SignalDesk';

const DISPLAY_FONT = "'Instrument Sans', 'Inter', sans-serif";

type Props = {
  open: boolean;
  onClose: () => void;
};

// DirectBuyMcpModal: add-on-only checkout entry for AI Trading (MCP). It configures
// cadence in-modal (prices sourced from listPrices via the billing catalog, with a
// contract fallback), then hands off to the shared checkout-v2 drawer at the Payment
// step — the same imperative useCheckoutStore pattern IntegrationCardsSection uses.
const DirectBuyMcpModal = ({ open, onClose }: Props) => {
  const theme = useTheme();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const { user } = useAuth() as { user?: { tcId?: string } };

  const catalog = useBillingStore((s) => s.catalog);
  const catalogError = useBillingStore((s) => s.catalogError);
  const catalogLoading = useBillingStore((s) => s.catalogLoading);
  const fetchCatalog = useBillingStore((s) => s.fetchCatalog);
  useEffect(() => {
    if (open && user?.tcId) fetchCatalog(user.tcId);
  }, [open, user?.tcId, fetchCatalog]);

  const cadences = useMemo(() => buildMcpCadences(catalog), [catalog]);
  const cycle = BILLING_CYCLES.monthly; // one package at launch
  const selectedCadence = cadences.find((c) => c.cycle === cycle) ?? cadences[0];

  // Dual-mode: this modal is the single AI Connect subscription surface, so it
  // also cancels. Ownership is resolved the same way the checkout drawer does
  // (findActiveMcp scans every active sub), which covers both shapes: a
  // standalone MCP subscription (subscriptionItemId null → cancel the whole sub
  // at period end) and a legacy MCP line inside a bundle sub (→ remove just that
  // line, immediately, with a prorated credit). The copy differs per shape and
  // each is accurate; picking the wrong one would misstate when access ends.
  const subscriptions = useBillingStore((s) => s.subscriptions);
  const mcpOwner = useMemo(() => findActiveMcp(subscriptions || [], catalog), [subscriptions, catalog]);
  // Only offer line-removal when the sub carries other lines; a sole-line sub
  // must cancel whole (the backend rejects removing a subscription's last item).
  const cancelItemId = mcpOwner && mcpOwner.lineCount > 1 ? mcpOwner.subscriptionItemId : null;
  const subscribed = Boolean(mcpOwner);
  const cancelCopy = cancelItemId ? CHECKOUT_COPY.cancel.mcpLine : CHECKOUT_COPY.cancel.mcp;
  const cancel = useCancelSubscription({
    subscriptionId: mcpOwner?.subscription?.id,
    subscriptionItemId: cancelItemId || undefined,
    subscription: mcpOwner?.subscription,
    successMessage: cancelCopy.success,
    errorMessage: cancelCopy.error
  });

  const primary = theme.palette.primary.main;
  const dark = theme.palette.mode === 'dark';

  // One package at launch — the card is a plan summary, not a chooser.
  const cycleCard = (value: string, label: string, price: string, per: string, note?: string) => {
    return (
      <Box
        key={value}
        sx={{
          flex: 1,
          borderRadius: '12px',
          p: 1.5,
          position: 'relative',
          border: '1px solid',
          borderColor: alpha(primary, 0.6),
          bgcolor: alpha(primary, dark ? 0.08 : 0.04),
          boxShadow: `0 0 0 3px ${alpha(primary, 0.12)}`
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
          <Typography sx={{ ...statusSx, color: ink(theme), textTransform: 'uppercase' }}>{label}</Typography>
          {note && (
            <Chip
              size="small"
              label={note}
              sx={{ height: 18, fontSize: 10, fontWeight: 600, color: ink(theme), bgcolor: alpha(primary, 0.12) }}
            />
          )}
          <Stack direction="row" spacing={0.5} sx={{ ml: 'auto' }} alignItems="center">
            {['MT4', 'MT5'].map((t) => (
              <Typography
                key={t}
                sx={{
                  ...statusSx,
                  fontSize: 9,
                  px: 0.75,
                  py: 0.25,
                  borderRadius: '6px',
                  color: ink(theme),
                  border: `1px solid ${alpha(primary, 0.35)}`
                }}
              >
                {t}
              </Typography>
            ))}
            <Box sx={{ display: 'flex', color: primary, fontSize: 15 }}>
              <CheckCircleFilled />
            </Box>
          </Stack>
        </Stack>
        <Typography
          sx={{
            fontFamily: DISPLAY_FONT,
            fontSize: 24,
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          {price}
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            {per}
          </Typography>
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Grow}
      transitionDuration={reduceMotion ? 0 : 220}
      PaperProps={{ sx: { borderRadius: '20px' } }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          // A quiet atmosphere band so the moment-of-purchase carries the same
          // stagecraft as the intro page it came from.
          backgroundImage: `radial-gradient(420px 140px at 18% -40px, ${alpha(primary, dark ? 0.18 : 0.1)}, transparent 70%)`
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, letterSpacing: '-0.02em' }}>
            Start trading by chat
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close" sx={mcpFocusSx(theme)}>
          <CloseOutlined />
        </IconButton>
      </Stack>
      <Divider />
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {/* the trust line — why this is safe to buy */}
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="flex-start"
          sx={{
            mb: 2.5,
            p: 1.5,
            borderRadius: '12px',
            border: `1px solid ${alpha(primary, 0.3)}`,
            bgcolor: alpha(primary, dark ? 0.08 : 0.04)
          }}
        >
          <Box sx={{ color: ink(theme), fontSize: 16, mt: '2px', flexShrink: 0 }}>
            <SafetyCertificateOutlined />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            Every order your AI places passes the <b>server-side policy gate</b> first — your lot caps and symbol limits can’t be exceeded,
            and each account stays off until you arm it.
          </Typography>
        </Stack>

        <Eyebrow sx={{ mb: 1 }}>{subscribed ? 'Your subscription' : 'Your plan'}</Eyebrow>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.25, mb: 2.5 }}>
          {cadences.map((c) =>
            cycleCard(c.cycle, c.label, formatCents(c.amountCents), intervalPriceLabel(c.interval, c.intervalCount), c.note)
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />
        {!subscribed && (
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 2.5 }}>
            <Typography variant="body2" color="text.secondary">
              Total due today
            </Typography>
            <Typography
              sx={{ fontFamily: DISPLAY_FONT, fontSize: 32, fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}
            >
              {formatCents(selectedCadence?.amountCents ?? 0)}
            </Typography>
          </Stack>
        )}

        {/* Live prices failed to resolve → don't let the user pay against the
            static contract fallback; the real Stripe amount could differ. This
            also fires when the catalog LOADED but carries no MCP price (e.g.
            the Stripe price is missing its mcp_<cycle> nickname): without a
            price id the drawer would stage nothing and quote $0. */}
        {!catalogLoading && !subscribed && (Boolean(catalog) || Boolean(catalogError)) && !selectedCadence?.priceId && (
          <Typography variant="caption" color="error" display="block" textAlign="center" sx={{ mb: 1 }}>
            Live pricing for AI Connect isn’t available right now.{' '}
            <Box
              component="button"
              type="button"
              onClick={() => user?.tcId && fetchCatalog(user.tcId)}
              sx={{ all: 'unset', cursor: 'pointer', textDecoration: 'underline', font: 'inherit', color: 'inherit' }}
            >
              Retry
            </Box>
          </Typography>
        )}
        {/* Already subscribed → this modal is the manage surface: the only
            action is cancelling, so the buy CTA is replaced rather than shown
            alongside (nothing here can be purchased twice — the backend rejects
            a second MCP subscription outright). */}
        {subscribed ? (
          <>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              color="error"
              sx={{ py: 1.3 }}
              disabled={cancel.busy || !mcpOwner?.subscription?.id}
              onClick={cancel.openModal}
            >
              {cancelCopy.confirmLabel}
            </Button>
            <CancelConfirmModal
              open={cancel.open}
              onClose={cancel.close}
              onConfirm={cancel.run}
              busy={cancel.busy}
              title={cancelCopy.title}
              description={cancelCopy.description}
              confirmLabel={cancelCopy.confirmLabel}
            />
          </>
        ) : (
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<ThunderboltOutlined />}
            sx={{ ...ctaSx(theme), py: 1.3 }}
            // No live price id → nothing the drawer could stage → it would quote
            // $0 and charge nothing. Block checkout until the catalog resolves.
            disabled={catalogLoading || !selectedCadence?.priceId}
            onClick={() => {
              // Hand off to the shared checkout-v2 drawer (mounted in MainLayout).
              // Reset to a clean cart, stage MCP for the chosen cadence, then jump
              // straight to Payment (requestedStep) — the modal already did the
              // "configure" step. CheckoutProvider re-hydrates the wiped cart when
              // the drawer session ends.
              //
              // mcpStandalone (not mcpEnabled) buys AI Connect as its OWN
              // subscription instead of appending a line to the buyer's base plan.
              // That is what makes discount codes possible here: a Stripe coupon is
              // subscription-scoped, so on the shared plan it would have discounted
              // the base plan too, and the checkout only forwards a coupon on
              // create ops. It also leaves the buyer's trading subs untouched.
              const store = useCheckoutStore.getState();
              store.resetSelection();
              store.setSelection({ billingCycle: cycle, mcpStandalone: true });
              store.openDrawer(CHECKOUT_STEPS.payment);
              onClose();
            }}
          >
            Continue to checkout
          </Button>
        )}
        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 1.5, fontWeight: 400 }}>
          {subscribed
            ? cancelCopy.description
            : 'For MetaTrader 4 & 5 accounts — more platforms soon. Doesn’t change your Copier or Analyzer plans. Cancel anytime.'}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default DirectBuyMcpModal;
